<template>
  <form class="vl-form" @submit.prevent="onSubmit">
    <vl-grid v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <Field v-slot="{ handleChange, value, errors }" name="file">
          <vl-upload
            id="data-upload"
            name="data-upload"
            :upload-label="$t('deliveryprocessdefinition.modals.import.uploadLabel')"
            allowed-file-types=".json"
            :max-filesize="5"
            :upload-drag-text="$t('vl-upload.uploadDragText')"
            :upload-response-error="$t('vl-upload.uploadResponseError')"
            :upload-invalid-file-type="$t('vl-upload.uploadInvalidFileType')"
            :upload-max-files-exceeded="$t('vl-upload.uploadMaxFilesExceeded')"
            :upload-files-hidden-close-label="$t('vl-upload.uploadFilesHiddenCloseLabel')"
            :upload-max-file-size="$t('vl-upload.uploadMaxFileSize')"
            url="/api/v1/configuration/deliveryprocessdefinition/import"
            :mod-error="!!errors.length"
            :max-files="1"
            :options="options"
            :value="value"
            @upload-file-added="handleChange"
            @upload-removed-file="removeFile"
          />
          <vl-form-message-error v-if="errors">{{ errors[0] }}</vl-form-message-error>
        </Field>
      </vl-column>
      <vl-form-column v-vl-spacer:bottom.large>
        <PbsSelectField
          name="deliveryprocessdefinition"
          :options-limit="4"
          :label="t('deliveryprocessdefinition.modals.import.attachDeliveryprocessdefinition')"
          :placeholder="$t('deliveryprocessdefinition.modals.import.deliveryprocessdefinition')"
          :options="deliveryProcessDefinitionOptions"
          :custom-label="getDeliveryProcessDefinitionLabel"
        />
      </vl-form-column>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="data.isSaving" :mod-loading="data.isSaving">{{
            $t('deliveryprocessdefinition.actions.import.label')
          }}</vl-button>
          <vl-button
            v-focus
            :mod-disabled="data.isSaving"
            mod-icon-before
            mod-secondary
            tag-name="a"
            @click.prevent="cancel"
          >
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
  </form>
</template>
<script lang="ts" setup>
import 'vue-i18n';
import * as yup from 'yup';
import { Field, useForm } from 'vee-validate';
import { Ref, onMounted, reactive, ref } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { useDeliveryProcessDefinitionDetailStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-detail.store';
import { DropzoneFile, DropzoneOptions } from 'dropzone';
import type Dropzone from 'dropzone';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import { DeliveryProcessDefinitionSearchResultDto } from '@/api/backoffice/clients';
import { useDeliveryProcessDefinitionListStore } from '@/modules/configuration/store/delivery-process-definition/delivery-process-definition-list.store';
import { getDeliveryProcessDefinitionLabel } from '@/modules/configuration/common/delivery-process-definition.common';

const store = useDeliveryProcessDefinitionDetailStore();
const deliveryProcessDefinitionStore = useDeliveryProcessDefinitionListStore();
const deliveryProcessDefinitionOptions: Ref<DeliveryProcessDefinitionSearchResultDto[]> = ref([]);

const { t } = useI18n();

const router = useRouter();

const data = reactive({
  isSaving: false
});

const validationSchema = yup.object({
  file: yup
    .mixed()
    .required(t('deliveryprocessdefinition.modals.import.validations.fileRequired'))
    .test('is-valid-type', t('deliveryprocessdefinition.modals.import.validations.invalidFileType'), (value) => {
      return value && (value as File).type === 'application/json';
    })
    .test(
      'is-valid-size',
      t('deliveryprocessdefinition.modals.import.validations.invalidFileSize'),
      (value) => value && (value as File).size <= 5 * 1000 * 1000
    ),
  deliveryprocessdefinition: yup.object().nullable()
});

const { handleSubmit, setFieldValue, resetForm } = useForm({
  validationSchema
});

onMounted(async () => {
  resetForm({
    values: {
      file: null,
      deliveryprocessdefinition: null
    }
  });

  deliveryProcessDefinitionOptions.value = await deliveryProcessDefinitionStore.getAll({ silent: true });
});

const options: DropzoneOptions = {
  init: function (): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const dropzone: Dropzone = this;
    // Hack to fix 'reuploading' of a file. Uploading a new file replaces the other one.
    dropzone.on('maxfilesexceeded', function (file: DropzoneFile) {
      dropzone.removeAllFiles();
      dropzone.addFile(file);
    });
  }
};

const removeFile = (): void => {
  setFieldValue('file', null, false);
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};

const onSuccess = async (values) => {
  const parentDefinitionId = values.deliveryprocessdefinition?.id;
  const id = await store.import(values.file, parentDefinitionId);

  closeModal();

  if (id) {
    router.push({
      name: 'deliveryprocessdefinition.detail',
      params: {
        id
      }
    });
  }
};

const onInvalidSubmit = ({ values, errors, results }) => {
  // console.log(values); // current form values
  // console.log(errors); // a map of field names and their first error message
  // console.log(results); // a detailed map of field names and their validation results
};

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit);
</script>
<style lang="scss" scoped></style>
